import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import AdminLayout from '../../components/layout/AdminLayout';
import OpsLayout from '../../components/layout/OpsLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Briefcase, Settings2, Plus, Trash2, Pencil, Check, X, CalendarPlus, Ban,
  MapPin, Users, Phone, ChevronDown, ChevronUp, HardHat, Shield, Store, Building2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT_META = {
  labour: { icon: HardHat, cls: 'bg-amber-500/10 text-amber-300 border-amber-500/30' },
  security: { icon: Shield, cls: 'bg-blue-500/10 text-blue-300 border-blue-500/30' },
  retail: { icon: Store, cls: 'bg-purple-500/10 text-purple-300 border-purple-500/30' },
};

const STATUS_CLS = {
  active: 'bg-green-500/10 text-green-400 border-green-500/30',
  cancelled: 'bg-red-500/10 text-red-400 border-red-500/30',
  completed: 'bg-muted text-muted-foreground border-border',
};

export default function AdminStaffingRequestsPage({ opsMode = false }) {
  const Layout = opsMode ? OpsLayout : AdminLayout;
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [positionsOpen, setPositionsOpen] = useState(false);
  const [editReq, setEditReq] = useState(null);
  const [dayDialog, setDayDialog] = useState(null); // { request, dayIndex|null, day }
  const [confirm, setConfirm] = useState(null); // { kind, request, dayIndex?, staff? }
  const [busy, setBusy] = useState(false);

  const fetchRequests = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/staffing-requests`);
      setRequests(res.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const visible = requests.filter((r) => filter === 'all' || r.status === filter);

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm.kind === 'cancel') {
        const res = await axios.post(`${API}/admin/staffing-requests/${confirm.request.id}/cancel`);
        toast.success(`Cancelled — ${res.data.jobs_removed} job(s) removed, ${res.data.jobs_archived} archived`);
      } else if (confirm.kind === 'delete') {
        const res = await axios.delete(`${API}/admin/staffing-requests/${confirm.request.id}`);
        toast.success(`Deleted — ${res.data.jobs_deleted} linked job(s) removed`);
      } else if (confirm.kind === 'delete-day') {
        await axios.delete(`${API}/admin/staffing-requests/${confirm.request.id}/days/${confirm.dayIndex}`);
        toast.success('Day removed');
      } else if (confirm.kind === 'remove-staff') {
        await axios.post(
          `${API}/open-shifts/${confirm.shiftId}/remove-staff?user_id=${confirm.staff.user_id}&single_day=true`
        );
        toast.success(`${confirm.staff.user_name} removed from this day`);
      }
      setConfirm(null);
      await fetchRequests();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  const confirmCopy = {
    cancel: {
      title: 'Cancel this request?',
      desc: 'Unclaimed jobs will be deleted; jobs with assigned staff are archived. The client will be emailed.',
      action: 'Cancel Request',
    },
    delete: {
      title: 'Permanently delete this request?',
      desc: 'The request, ALL its jobs and any assigned employee shifts will be deleted. Assigned staff and the client will be emailed. This cannot be undone.',
      action: 'Delete Permanently',
    },
    'delete-day': {
      title: 'Remove this day?',
      desc: 'Its job is deleted (or archived if staff are already assigned). The client will be emailed.',
      action: 'Remove Day',
    },
    'remove-staff': {
      title: `Remove ${confirm?.staff?.user_name || 'this employee'} from this day?`,
      desc: 'The slot is freed and their shift for this day is removed.',
      action: 'Remove Staff',
    },
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-4 max-w-4xl mx-auto">
        <div className="border-b border-border pb-4 flex items-center gap-3">
          <Briefcase className="w-6 h-6 text-primary" strokeWidth={1.5} />
          <div>
            <h1 className="text-2xl font-barlow tracking-widest text-foreground">STAFFING REQUESTS</h1>
            <p className="text-xs text-muted-foreground mt-1">
              {opsMode
                ? 'Read-only — client staffing requirements to organise your shifts around.'
                : 'Incoming client requests for Labour, Security & Retail staff.'}
            </p>
          </div>
          {!opsMode && (
          <Button
            variant="outline"
            onClick={() => setPositionsOpen(true)}
            data-testid="manage-positions-btn"
            className="ml-auto border-primary/40 text-primary hover:bg-primary/10 rounded-sm text-xs uppercase tracking-wider"
          >
            <Settings2 className="w-4 h-4 mr-1.5" /> Manage Positions
          </Button>
          )}
        </div>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="bg-muted/40 border border-border rounded-sm">
            <TabsTrigger value="all" data-testid="filter-all">All ({requests.length})</TabsTrigger>
            <TabsTrigger value="active" data-testid="filter-active">Active ({requests.filter((r) => r.status === 'active').length})</TabsTrigger>
            <TabsTrigger value="cancelled" data-testid="filter-cancelled">Cancelled</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <p className="py-12 text-center text-muted-foreground text-sm">Loading…</p>
        ) : visible.length === 0 ? (
          <p className="py-12 text-center text-muted-foreground text-sm" data-testid="admin-requests-empty">No staffing requests here yet.</p>
        ) : (
          <div className="space-y-3">
            {visible.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                readOnly={opsMode}
                isOpen={!!expanded[r.id]}
                onToggle={() => setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))}
                onEdit={() => setEditReq(r)}
                onAddDay={() => setDayDialog({ request: r, dayIndex: null, day: null })}
                onEditDay={(i, d) => setDayDialog({ request: r, dayIndex: i, day: d })}
                onDeleteDay={(i) => setConfirm({ kind: 'delete-day', request: r, dayIndex: i })}
                onCancel={() => setConfirm({ kind: 'cancel', request: r })}
                onDelete={() => setConfirm({ kind: 'delete', request: r })}
                onRemoveStaff={(shiftId, staff) => setConfirm({ kind: 'remove-staff', request: r, shiftId, staff })}
              />
            ))}
          </div>
        )}
      </div>

      <PositionsManagerDialog open={positionsOpen} onClose={() => setPositionsOpen(false)} />
      <EditRequestDialog request={editReq} onClose={() => setEditReq(null)} onSaved={fetchRequests} />
      <DayDialog config={dayDialog} onClose={() => setDayDialog(null)} onSaved={fetchRequests} />

      <AlertDialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent className="bg-card border-border rounded-sm" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm ? confirmCopy[confirm.kind].title : ''}</AlertDialogTitle>
            <AlertDialogDescription>{confirm ? confirmCopy[confirm.kind].desc : ''}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="confirm-cancel-btn">Back</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runConfirm(); }}
              disabled={busy}
              className="rounded-sm bg-red-600 hover:bg-red-500 text-white"
              data-testid="confirm-action-btn"
            >
              {busy ? 'Working…' : (confirm ? confirmCopy[confirm.kind].action : '')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function RequestCard({ request: r, isOpen, onToggle, onEdit, onAddDay, onEditDay, onDeleteDay, onCancel, onDelete, onRemoveStaff, readOnly = false }) {
  const Meta = CAT_META[r.category] || CAT_META.labour;
  const isActive = r.status === 'active';
  return (
    <Card className="bg-card border-border rounded-sm" data-testid={`admin-request-${r.id}`}>
      <CardContent className="p-4">
        <div
          className="flex items-start gap-3 cursor-pointer"
          onClick={onToggle}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter') onToggle(); }}
          data-testid={`admin-row-${r.id}`}
        >
          <span className="w-10 h-10 rounded-full bg-black border border-primary/30 flex items-center justify-center shrink-0">
            <Meta.icon className="w-4 h-4 text-primary" strokeWidth={1.75} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <p className="text-sm sm:text-base font-semibold">{r.site_name}</p>
              <Badge className={`text-[10px] uppercase border ${Meta.cls}`}>{r.category}</Badge>
              <Badge className={`text-[10px] uppercase border ${STATUS_CLS[r.status] || ''}`}>{r.status}</Badge>
            </div>
            <p className="mt-0.5 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-0.5">
              <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{r.company_name}</span>
              <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{r.address}, {r.postcode}</span>
              <span className="flex items-center gap-1"><Users className="w-3 h-3" />{r.total_staff_requested} staff · {r.days?.length} row(s)</span>
            </p>
          </div>
          <button type="button" onClick={(e) => { e.stopPropagation(); onToggle(); }} data-testid={`admin-toggle-${r.id}`} className="text-muted-foreground hover:text-foreground shrink-0">
            {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {isOpen && (
          <div className="mt-3 border-t border-border pt-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><Phone className="w-3 h-3" /><strong>{r.contact_person}</strong> — {r.contact_number}</span>
              {r.purchase_order && <span><strong>PO:</strong> {r.purchase_order}</span>}
              {r.reference_number && <span><strong>Ref:</strong> {r.reference_number}</span>}
              {r.additional_info && <span className="sm:col-span-2"><strong>Info:</strong> {r.additional_info}</span>}
              <span>Submitted {(() => { try { return format(parseISO(r.created_at), 'dd MMM yyyy HH:mm'); } catch { return r.created_at; } })()}</span>
              {r.cancelled_by === 'admin' && <span className="text-red-400">Cancelled by admin{r.cancelled_by_name ? ` (${r.cancelled_by_name})` : ''}</span>}
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground uppercase tracking-wider">
                    <th className="py-1 pr-3">Date</th><th className="py-1 pr-3">Time</th>
                    <th className="py-1 pr-3">Position</th><th className="py-1 pr-3">Staff</th>
                    <th className="py-1 pr-3">Assigned</th><th className="py-1" />
                  </tr>
                </thead>
                <tbody>
                  {(r.days || []).map((d, i) => (
                    <tr key={i} className="border-t border-border align-top" data-testid={`day-row-${r.id}-${i}`}>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{(() => { try { return format(parseISO(d.date), 'EEE dd MMM'); } catch { return d.date; } })()}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">{d.start_time}–{d.end_time}</td>
                      <td className="py-1.5 pr-3">{d.position}</td>
                      <td className="py-1.5 pr-3 whitespace-nowrap">
                        <span className={d.claimed_count >= d.staff_required ? 'text-green-400' : 'text-amber-300'}>
                          {d.claimed_count || 0}/{d.staff_required}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3">
                        {(d.claimed_by || []).length === 0 ? (
                          <span className="text-muted-foreground">—</span>
                        ) : (
                          <span className="flex flex-wrap gap-1">
                            {(d.claimed_by || []).map((c) => (
                              <Badge key={c.user_id} variant="outline" className="text-[10px] border-border text-foreground gap-1 pr-1" data-testid={`staff-chip-${r.id}-${i}-${c.user_id}`}>
                                {c.user_name}
                                {isActive && (
                                  <button
                                    type="button"
                                    onClick={() => onRemoveStaff(d.open_shift_id, c)}
                                    className="text-muted-foreground hover:text-red-400"
                                    title="Remove from this day"
                                    data-testid={`remove-staff-${r.id}-${i}-${c.user_id}`}
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                )}
                              </Badge>
                            ))}
                          </span>
                        )}
                      </td>
                      <td className="py-1.5 whitespace-nowrap text-right">
                        {isActive && !readOnly && (
                          <>
                            <Button size="icon" variant="ghost" onClick={() => onEditDay(i, d)} className="h-6 w-6 text-muted-foreground hover:text-primary" data-testid={`edit-day-${r.id}-${i}`}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={() => onDeleteDay(i)} className="h-6 w-6 text-muted-foreground hover:text-red-400" data-testid={`delete-day-${r.id}-${i}`}>
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!readOnly && (
            <div className="flex flex-wrap gap-2 pt-1">
              {isActive && (
                <>
                  <Button size="sm" variant="outline" onClick={onEdit} className="rounded-sm text-xs border-border" data-testid={`edit-request-${r.id}`}>
                    <Pencil className="w-3.5 h-3.5 mr-1" /> Edit Details
                  </Button>
                  <Button size="sm" variant="outline" onClick={onAddDay} className="rounded-sm text-xs border-border" data-testid={`add-day-${r.id}`}>
                    <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Add Day
                  </Button>
                  <Button size="sm" variant="outline" onClick={onCancel} className="rounded-sm text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid={`cancel-request-${r.id}`}>
                    <Ban className="w-3.5 h-3.5 mr-1" /> Cancel Request
                  </Button>
                </>
              )}
              <Button size="sm" variant="outline" onClick={onDelete} className="rounded-sm text-xs border-red-500/40 text-red-400 hover:bg-red-500/10 ml-auto" data-testid={`delete-request-${r.id}`}>
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EditRequestDialog({ request, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (request) {
      setForm({
        site_name: request.site_name || '', address: request.address || '', postcode: request.postcode || '',
        contact_person: request.contact_person || '', contact_number: request.contact_number || '',
        purchase_order: request.purchase_order || '', reference_number: request.reference_number || '',
        additional_info: request.additional_info || '',
      });
    }
  }, [request]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/staffing-requests/${request.id}`, form);
      toast.success('Request updated — jobs synced, client emailed');
      onClose();
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!request} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-h-[85vh] overflow-y-auto" data-testid="edit-request-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-widest uppercase text-primary text-lg">Edit Request</DialogTitle>
          <DialogDescription>Changes sync to the linked jobs; the client is emailed.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['site_name', 'Site / Event Name', 'sm:col-span-2'],
            ['address', 'Full Address', ''],
            ['postcode', 'Postcode', ''],
            ['contact_person', 'Contact Person', ''],
            ['contact_number', 'Contact Number', ''],
            ['purchase_order', 'Purchase Order', ''],
            ['reference_number', 'Reference Number', ''],
          ].map(([f, label, cls]) => (
            <div key={f} className={`space-y-1 ${cls}`}>
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</Label>
              <Input value={form[f] || ''} onChange={(e) => setForm({ ...form, [f]: e.target.value })} className="rounded-sm h-9 text-sm" data-testid={`edit-req-${f}`} />
            </div>
          ))}
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Additional Information</Label>
            <Textarea rows={2} value={form.additional_info || ''} onChange={(e) => setForm({ ...form, additional_info: e.target.value })} className="rounded-sm text-sm" data-testid="edit-req-additional_info" />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="rounded-sm text-xs">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm text-xs uppercase tracking-wider" data-testid="edit-req-save-btn">
            <Check className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DayDialog({ config, onClose, onSaved }) {
  const isEdit = config?.dayIndex !== null && config?.dayIndex !== undefined;
  const [form, setForm] = useState({ date: '', start_time: '', end_time: '', position: '', staff_required: 1, notes: '' });
  const [positions, setPositions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!config) return;
    const d = config.day;
    setForm(d
      ? { date: d.date, start_time: d.start_time, end_time: d.end_time, position: d.position, staff_required: d.staff_required, notes: d.notes || '' }
      : { date: '', start_time: '', end_time: '', position: '', staff_required: 1, notes: '' });
    axios.get(`${API}/staffing-positions?category=${config.request.category}`)
      .then((res) => setPositions(res.data || []))
      .catch(() => setPositions([]));
  }, [config]);

  const save = async () => {
    if (!form.date || !form.start_time || !form.end_time || !form.position || !(parseInt(form.staff_required, 10) >= 1)) {
      toast.error('Complete date, times, position and staff number');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, staff_required: parseInt(form.staff_required, 10), notes: form.notes || null };
      if (isEdit) {
        await axios.put(`${API}/admin/staffing-requests/${config.request.id}/days/${config.dayIndex}`, payload);
        toast.success('Day updated — job synced, affected staff & client notified');
      } else {
        await axios.post(`${API}/admin/staffing-requests/${config.request.id}/days`, payload);
        toast.success('Day added — new job created, employees being notified');
      }
      onClose();
      await onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save day');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!config} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm" data-testid="day-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-widest uppercase text-primary text-lg">
            {isEdit ? 'Edit Day' : 'Add Day'}
          </DialogTitle>
          <DialogDescription>
            {isEdit ? 'The linked job and assigned staff shifts are updated automatically.' : 'A new claimable job is created and employees are notified.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Date *</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm h-9 text-sm" data-testid="day-form-date" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Staff # *</Label>
            <Input type="number" min="1" value={form.staff_required} onChange={(e) => setForm({ ...form, staff_required: e.target.value })} className="rounded-sm h-9 text-sm" data-testid="day-form-staff" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Start *</Label>
            <Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="rounded-sm h-9 text-sm" data-testid="day-form-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">End *</Label>
            <Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="rounded-sm h-9 text-sm" data-testid="day-form-end" />
          </div>
          <div className="space-y-1 col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Position *</Label>
            <Select value={form.position} onValueChange={(v) => setForm({ ...form, position: v })}>
              <SelectTrigger className="rounded-sm h-9 text-sm" data-testid="day-form-position">
                <SelectValue placeholder="Select position" />
              </SelectTrigger>
              <SelectContent className="bg-card border-border">
                {positions.map((p) => (
                  <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 col-span-2">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm h-9 text-sm" data-testid="day-form-notes" />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} className="rounded-sm text-xs">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm text-xs uppercase tracking-wider" data-testid="day-form-save-btn">
            <Check className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : (isEdit ? 'Save Day' : 'Add Day')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PositionsManagerDialog({ open, onClose }) {
  const [cat, setCat] = useState('labour');
  const [positions, setPositions] = useState([]);
  const [newName, setNewName] = useState('');
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');

  const fetchPositions = useCallback(async () => {
    const res = await axios.get(`${API}/admin/staffing-positions`).catch(() => ({ data: [] }));
    setPositions(res.data || []);
  }, []);

  useEffect(() => { if (open) fetchPositions(); }, [open, fetchPositions]);

  const inCat = positions.filter((p) => p.category === cat);

  const add = async () => {
    if (!newName.trim()) return;
    try {
      await axios.post(`${API}/admin/staffing-positions`, { category: cat, name: newName.trim() });
      toast.success('Position added');
      setNewName('');
      await fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add');
    }
  };

  const saveEdit = async () => {
    try {
      await axios.put(`${API}/admin/staffing-positions/${editId}`, { name: editName.trim() });
      toast.success('Position renamed');
      setEditId(null);
      await fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rename');
    }
  };

  const remove = async (p) => {
    try {
      await axios.delete(`${API}/admin/staffing-positions/${p.id}`);
      toast.success('Position removed');
      await fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to remove');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-card border-border rounded-sm max-h-[85vh] overflow-y-auto" data-testid="positions-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-widest uppercase text-primary text-lg">Manage Positions</DialogTitle>
          <DialogDescription>Positions clients can request, per category.</DialogDescription>
        </DialogHeader>

        <Tabs value={cat} onValueChange={setCat}>
          <TabsList className="bg-muted/40 border border-border rounded-sm w-full">
            <TabsTrigger value="labour" className="flex-1" data-testid="pos-tab-labour">Labour</TabsTrigger>
            <TabsTrigger value="security" className="flex-1" data-testid="pos-tab-security">Security</TabsTrigger>
            <TabsTrigger value="retail" className="flex-1" data-testid="pos-tab-retail">Retail</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New position name" className="rounded-sm" data-testid="new-position-input" onKeyDown={(e) => e.key === 'Enter' && add()} />
          <Button onClick={add} className="bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm shrink-0" data-testid="add-position-btn">
            <Plus className="w-4 h-4" />
          </Button>
        </div>

        <div className="space-y-1.5">
          {inCat.map((p) => (
            <div key={p.id} className="flex items-center gap-2 px-3 py-2 bg-muted/20 border border-border rounded-sm" data-testid={`position-row-${p.id}`}>
              {editId === p.id ? (
                <>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="rounded-sm h-8 text-sm" data-testid="edit-position-input" onKeyDown={(e) => e.key === 'Enter' && saveEdit()} />
                  <Button size="icon" variant="ghost" onClick={saveEdit} className="h-8 w-8 text-green-400" data-testid="save-position-btn"><Check className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditId(null)} className="h-8 w-8 text-muted-foreground"><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <span className="text-sm flex-1">{p.name}</span>
                  <Button size="icon" variant="ghost" onClick={() => { setEditId(p.id); setEditName(p.name); }} className="h-8 w-8 text-muted-foreground hover:text-primary" data-testid={`edit-position-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(p)} className="h-8 w-8 text-muted-foreground hover:text-red-400" data-testid={`delete-position-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </>
              )}
            </div>
          ))}
          {inCat.length === 0 && <p className="text-xs text-muted-foreground text-center py-4">No positions in this category.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
